'use client';

import type {
  ChiliPiperOptions,
  ChiliPiperPayload,
  ChiliPiperRequest,
} from '@/types/chilipiper';
import { useCallback, useState } from 'react';

// https://help.chilipiper.com/hc/en-us/articles/30935152032275-Configuring-Concierge-REST-API#h_01JPARE4XXJ0M6FTNH53ZETC11
export function useChiliPiper() {
  const [isLoading, setIsLoading] = useState(false);

  const openScheduler = useCallback(
    async (
      payload: ChiliPiperPayload,
      options: ChiliPiperOptions = {},
      onCompleted: (url: string, features?: string) => void
    ) => {
      if (isLoading) return;

      setIsLoading(true);

      try {
        const ORG = process.env.NEXT_PUBLIC_CHILIPIPER_ORG;
        const ROUTER = process.env.NEXT_PUBLIC_CHILIPIPER_ROUTER;
        const FALLBACK = process.env.NEXT_PUBLIC_CHILIPIPER_FALLBACK_URL;

        if (!ROUTER) {
          console.error('Missing NEXT_PUBLIC_CHILIPIPER_ROUTER env var');
          if (FALLBACK) onCompleted(FALLBACK, '_blank');
          return;
        }

        // Using the REST API endpoint format from the documentation
        const CHILIPIPER_BASE_URL = `https://${ORG}.chilipiper.com/concierge-router`;

        const restUrl = `${CHILIPIPER_BASE_URL}/${ROUTER}/rest`;

        const body: ChiliPiperRequest = {
          form: {
            // Using form field names as they appear in the Concierge router configuration
            firstname: payload.firstName || '',
            lastname: payload.lastName || '',
            email: payload.email || '',
            phone: payload.phone || '',
            location: payload.location || '',
            source: payload.source || 'Website',
            note: payload.note || '',
            age: payload.age || '',
            today_day_hidden: new Date().toISOString().split('T')[0],
            afterhours: '',
            // Custom field mappings - you'll need to replace these with actual field IDs from your Concierge router
            '191b047e-2e1b-4cce-84d1-292f0bd001d8': payload.postalCode || '', // Zip Code
            '83c32c09-3a36-40df-97ee-0252db3b4200': payload.postalCode || '', // Postal Code
          },
          options: {
            trigger: 'InAppButton',
            dynamicRedirectLink: window.location.origin,
            ...options,
            // Pass metadata as custom event fields
            event: {
              ...payload.metadata,
              ...options.event,
            },
          },
        };

        const response = await fetch(restUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          console.error('ChiliPiper API error:', {
            status: response.status,
            statusText: response.statusText,
          });
          throw new Error('Something went wrong. Please try again later');
        }

        const result = await response.json();

        if (result.routingLink || result.url) {
          const calendarUrl = result.routingLink || result.url;
          onCompleted(
            calendarUrl,
            'width=800,height=600,scrollbars=yes,resizable=yes'
          );
        } else {
          console.warn('No routing link returned from ChiliPiper');
          if (FALLBACK) {
            onCompleted(FALLBACK);
          }
        }
      } catch (error) {
        console.error('ChiliPiper submission error:', error);
        const FALLBACK = process.env.NEXT_PUBLIC_CHILIPIPER_FALLBACK_URL;
        if (FALLBACK) {
          onCompleted(FALLBACK);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading]
  );

  return {
    ready: true, // Always ready since we don't need to load a script
    openScheduler,
    isLoading,
  };
}
